import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, Receipt, RefreshCw, Trash2, Eye, ChevronRight, BarChart3, Calculator } from "lucide-react";
import { PageHeader }  from "@/components/shared/PageHeader";
import {
  useGastos, useDeleteGasto,
  CategoriaBadge, GastoFiltersBar,
} from "@/features/expenses";
import { PAYMENT_METHOD_LABELS, type ExpenseFilters } from "@cold-crew/shared";
import { formatCurrency, formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/gastos/")({
  component: GastosPage,
});

function GastosPage() {
  const [filters, setFilters] = useState<Partial<ExpenseFilters>>({ page: 1, limit: 20 });

  const { data, isLoading, isError, refetch, isFetching } = useGastos(filters);
  const deleteGasto = useDeleteGasto();

  const gastos     = data?.data       ?? [];
  const total      = data?.total      ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const page       = data?.page       ?? 1;

  const totalMonto = gastos.reduce((s, g) => s + g.monto, 0);

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar este gasto? La acción es reversible.")) return;
    await deleteGasto.mutateAsync(id);
  }

  return (
    <div className="min-h-full space-y-5 bg-[#f4f1ea] p-6">

      {/* Header — igual que antes */}
      <PageHeader
        title="Gastos"
        description={`${total} gasto${total !== 1 ? "s" : ""} registrado${total !== 1 ? "s" : ""}`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} disabled={isFetching}
              className="flex size-10 items-center justify-center rounded-xl border border-[#ddd8cf] bg-white text-[#4f4a44] transition-colors hover:bg-[#f8f6f1] disabled:opacity-50" title="Actualizar">
              <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            </button>
            {/* Botón Nuevo gasto — mismo degradado del Hero/Sidebar */}
            <Link to="/gastos/nuevo"
              style={{ background: "#090909" }}
              className="flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white transition-opacity hover:opacity-90">
              <Plus className="size-4" /> Nuevo gasto
            </Link>
          </div>
        }
      />

      {/* Stats — mismo lenguaje visual aprobado en Pedidos */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {[
          { label: "Total del período", value: formatCurrency(totalMonto), icon: Receipt, box: "bg-[#fff0e7]", iconColor: "text-[#f47b2a]" },
          { label: "Registros", value: String(total), icon: BarChart3, box: "bg-[#e8f6ed]", iconColor: "text-[#176c4f]" },
          { label: "Promedio", value: total > 0 ? formatCurrency(totalMonto / total) : "$0", icon: Calculator, box: "bg-[#f2e9fb]", iconColor: "text-[#8b4ac6]" },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="flex min-h-[92px] items-center gap-4 rounded-2xl border border-[#ded9cf] bg-white px-5 py-4">
              <div className={cn("flex size-11 shrink-0 items-center justify-center rounded-full", stat.box)}>
                <Icon className={cn("size-5", stat.iconColor)} strokeWidth={2} />
              </div>
              <div className={cn(
  "min-w-0",
  (stat.label === "Total del período" || stat.label === "Promedio") && "pr-2 sm:pr-0"
)}>
  <p className="text-[12px] font-semibold text-[#77726a]">{stat.label}</p>
                <p
  className={cn(
    "mt-1 whitespace-nowrap font-black leading-none tracking-[-0.035em] text-[#171614]",
    stat.label === "Total del período" || stat.label === "Promedio"
      ? "text-[18px] sm:text-[24px]"
      : "text-[24px]"
  )}
>
  {stat.value}
</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <GastoFiltersBar filters={filters} onChange={setFilters} />

      {/* ══ MÓVIL: lista de tarjetas (nuevo) ══ */}
      <div className="sm:hidden rounded-lg border border-border overflow-hidden bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Cargando gastos…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-sm text-destructive">Error al cargar gastos.</p>
            <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
          </div>
        ) : gastos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <Receipt className="size-8 opacity-40" strokeWidth={1.25} />
            <p className="text-sm">Sin gastos registrados</p>
            {(filters.search || filters.categoria) && (
              <button onClick={() => setFilters({ page: 1, limit: 20 })}
                className="text-xs text-primary hover:underline">Limpiar filtros</button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {gastos.map((gasto) => (
                <li key={gasto.id} className="flex items-center gap-3 px-4 py-3.5">
                  {/* Ícono */}
                  <div className="shrink-0 size-9 rounded-xl bg-destructive/10 flex items-center justify-center">
                    <Receipt className="size-4 text-destructive" />
                  </div>
                  {/* Info — tap para detalle */}
                  <Link to="/gastos/$gastoId" params={{ gastoId: String(gasto.id) }}
                    className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CategoriaBadge categoria={gasto.categoria} />
                      <span className="text-[11px] text-muted-foreground">{formatDate(gasto.fecha)}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground truncate mt-0.5">
                      {gasto.descripcion || <span className="text-muted-foreground italic">Sin descripción</span>}
                    </p>
                    {gasto.proveedor && (
                      <p className="text-[11px] text-muted-foreground truncate">{gasto.proveedor}</p>
                    )}
                  </Link>
                  {/* Monto */}
                  <Link to="/gastos/$gastoId" params={{ gastoId: String(gasto.id) }}
                    className="shrink-0 text-right">
                    <p className="font-bold text-sm tabular-nums text-destructive">{formatCurrency(gasto.monto)}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{PAYMENT_METHOD_LABELS[gasto.metodo_pago]}</p>
                  </Link>
                 {/* Acciones */}
                  <div className="shrink-0 flex items-center gap-0.5">
                    <ChevronRight className="size-4 text-muted-foreground/30" />
                    <button onClick={() => handleDelete(gasto.id)}
                      disabled={deleteGasto.isPending}
                      className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40">
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {/* Footer total móvil */}
            <div className="px-4 py-2.5 border-t border-border bg-muted/20 flex justify-between text-xs text-muted-foreground">
              <span>Mostrando {gastos.length} de {total}</span>
              <span className="font-bold text-foreground">{formatCurrency(totalMonto)}</span>
            </div>
          </>
        )}
      </div>

      {/* ══ ESCRITORIO: tabla original ══ */}
      <div className="hidden sm:block overflow-hidden rounded-2xl border border-[#ded9cf] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Cargando gastos…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm text-destructive">Error al cargar gastos.</p>
            <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
          </div>
        ) : gastos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
            <Receipt className="size-8 opacity-40" strokeWidth={1.25} />
            <p className="text-sm">Sin gastos registrados</p>
            {(filters.search || filters.categoria) && (
              <button onClick={() => setFilters({ page: 1, limit: 20 })}
                className="text-xs text-primary hover:underline">Limpiar filtros</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {/* Encabezado de tabla — mismo degradado del Hero/Sidebar, texto blanco */}
              <thead>
                <tr className="bg-[#090909]">
                  {["Fecha","Categoría","Descripción","Monto","Método de pago",""].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold text-white/80 uppercase tracking-[0.04em] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e3da]">
                {gastos.map((gasto) => (
                  <tr key={gasto.id} className="group transition-colors hover:bg-[#faf8f4]">
                    <td className="px-4 py-3.5 text-[#77726a] whitespace-nowrap">{formatDate(gasto.fecha)}</td>
                    <td className="px-4 py-3"><CategoriaBadge categoria={gasto.categoria} /></td>
                    <td className="px-4 py-3 text-foreground max-w-[220px] truncate">
                      {gasto.descripcion || <span className="text-muted-foreground italic">Sin descripción</span>}
                      {gasto.proveedor && <span className="block text-xs text-muted-foreground font-normal truncate">{gasto.proveedor}</span>}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-foreground whitespace-nowrap">
                      {formatCurrency(gasto.monto)}
                      {gasto.comprobante && <span className="block text-xs text-muted-foreground font-normal">{gasto.comprobante}</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize whitespace-nowrap">{PAYMENT_METHOD_LABELS[gasto.metodo_pago]}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link to="/gastos/$gastoId" params={{ gastoId: String(gasto.id) }}
                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Ver detalle">
                          <Eye className="size-3.5" />
                        </Link>
                        <button onClick={() => handleDelete(gasto.id)} disabled={deleteGasto.isPending}
                          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40" title="Eliminar">
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {gastos.length > 0 && (
                <tfoot>
                  <tr className="border-t border-border bg-muted/20">
                    <td colSpan={3} className="px-4 py-2 text-xs text-muted-foreground">Mostrando {gastos.length} de {total}</td>
                    <td className="px-4 py-2 tabular-nums font-bold text-sm text-foreground whitespace-nowrap">{formatCurrency(totalMonto)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Paginación — igual que antes */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Página {page} de {totalPages} — {total} registros</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-40">‹ Anterior</button>
            <button onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-40">Siguiente ›</button>
          </div>
        </div>
      )}
    </div>
  );
}


